/**
 * F3's surface — a recall riding on a product that is not published.
 *
 * [[2026-08-06 A Recall on a Draft Product]] ruled that publishing a recall against an
 * unpublished product **succeeds**, with a `RECALL_NOT_RESOLVING` advisory, and the note's
 * *What this binds* section names c34 by name: the copy must say it **in scan terms**, and
 * *"the remedy it names ('publish the product') must be a path the same screen offers."*
 *
 * ## The advisory is a derived state, not a stored one
 *
 * Nothing in the database says "this recall is not resolving". It is the conjunction of two
 * rows — an `active` recall, and a product whose status is not `published` — and that
 * conjunction can be broken from either side: publish the product, or lift the recall.
 * Deriving it here means the advisory disappears the moment either happens, without anything
 * having to remember to clear it.
 *
 * ## The absence is half the deliverable, and it is why this is a function
 *
 * The ruling demands the companion assertion: **absent for a published product's recall.**
 * A safety advisory that always renders is one nobody reads — the same failure as an
 * attention stack that always has an item, one screen over. `recallAdvisory` returning
 * `null` is a claim a test can make about a value; "the banner did not appear" is a claim
 * about a rendered tree that passes just as well when the component crashed.
 */
import { WARNINGS, type ProductStatus, type RecallStatus } from "@packagepage/shared";

/** Just what this decision reads. Structural, so a test builds one without the server. */
export interface AdvisoryInput {
  readonly productStatus: ProductStatus;
  /**
   * When this product was first published, or `null` if it never has been.
   *
   * **This, not `status`, is what decides whether the notice is being served** — and the
   * first version of this file got it wrong by keying on `status !== "published"`.
   *
   * `archived` is reachable from **both** `draft` and `published` (`TRANSITIONS` in
   * `ops/products.ts`), so the status alone says nothing about whether an entry exists.
   * And archiving is **resolution-neutral by ruling** (R2, 2026-08-06): `retireProduct`
   * re-emits the entry *unchanged*, so a product archived after publishing goes on
   * answering exactly what it answered before — including the recall's materialised links.
   * Firing the advisory there would have told a brand their live recall was not resolving
   * while it was, which is the one direction a safety advisory must never be wrong in.
   *
   * `published_at` is set by `coalesce(published_at, now())` on publish and is never
   * cleared, so `null` means *"nothing has ever been published for this code"* — which is
   * precisely the claim the advisory's copy makes.
   */
  readonly publishedAt: string | null;
  readonly gtin14: string;
  readonly recalls: readonly {
    id: string;
    productId: string;
    status: RecallStatus;
    scope: string;
    lots: readonly string[];
  }[];
  /** This product's id — recalls arrive org-wide from `recalls.list`. */
  readonly productId: string;
}

export interface RecallAdvisory {
  /** The registry's code, so the screen switches on a discriminant rather than on prose. */
  readonly code: "RECALL_NOT_RESOLVING";
  readonly title: string;
  /** What printed codes are doing right now — first, and before the remedy. */
  readonly detail: string;
  /**
   * Whether *publishing the product* is the remedy.
   *
   * False for the terminal state, where publishing is not a transition the API allows and
   * the screen must not offer a button that would be refused. `screens.md`'s rule about
   * remedies that do not exist applies to a button the API would decline exactly as much
   * as to one nothing has built.
   */
  readonly publishFixes: boolean;
  /** The recalls this is about. Plural: nothing stops two being live on one product. */
  readonly recallIds: readonly string[];
}

/**
 * The advisory, or `null` when there is nothing to say.
 *
 * `null` on every one of: a product whose code is answering (published, **or archived after
 * having been published** — see {@link AdvisoryInput.publishedAt}), a product with no
 * recalls, and a product whose recalls are all lifted. Each is a different way to be fine,
 * and none of them is a state this screen should decorate.
 */
export function recallAdvisory(input: AdvisoryInput): RecallAdvisory | null {
  const live = input.recalls.filter(
    (r) => r.productId === input.productId && r.status === "active",
  );
  if (live.length === 0) return null;

  // Two states in which a live recall is not reaching a scanner, and they are **not** the
  // same sentence. The advisory used to say "404" for both, which is false for a retired
  // product — its code answers, with the platform's "no longer available" page.
  const neverPublished = input.publishedAt === null;
  const terminal = input.productStatus === "tombstoned";
  if (!neverPublished && !terminal) return null;

  // The title comes from the shared registry rather than being written here, so the
  // advisory a screen shows and the advisory the API emits are one string. The API already
  // answers this code on the publish path; a second wording would make them look like two
  // different problems to a user who met both.
  const title = WARNINGS.RECALL_NOT_RESOLVING.title;
  const recallIds = live.map((r) => r.id);

  if (terminal) {
    return {
      code: "RECALL_NOT_RESOLVING",
      title,
      detail:
        `A scan of ${input.gtin14} lands on the platform's "no longer available" page — ` +
        `this product was retired permanently, and a retired code carries no links, so ` +
        `the notice is not what a scanner sees. The recall is recorded and its evidence ` +
        `trail is intact. Retiring cannot be undone, so if this notice needs to reach ` +
        `people, it has to reach them somewhere other than the code.`,
      publishFixes: false,
      recallIds,
    };
  }

  return {
    code: "RECALL_NOT_RESOLVING",
    title,
    detail:
      `A scan of ${input.gtin14} gets a 404 today — nothing has ever been published for ` +
      `this code, so there is no page for the notice to take precedence over and the ` +
      `notice is not being served. The recall is recorded and its evidence trail is ` +
      `running; it starts reaching people the moment the product is published, within ` +
      `seconds.`,
    publishFixes: true,
    recallIds,
  };
}
